import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { api } from '../../lib/api'
import { formatDate } from '../../lib/format'
import { DisplayStyle } from '../../lib/types'
import { Icon } from '../Icon'

interface Suggestion {
  title: string
  slug: string
  date: string
}

/** Arrow keys, Enter and Escape for a suggestion list; returns true when it used the key */
function suggestKeys(e: KeyboardEvent, count: number, setActive: (f: (a: number) => number) => void, choose: () => void, close: () => void): boolean {
  if (e.key === 'Escape') close()
  else if (count && e.key === 'ArrowDown') setActive((a) => (a + 1) % count)
  else if (count && e.key === 'ArrowUp') setActive((a) => (a - 1 + count) % count)
  else if (count && e.key === 'Enter') choose()
  else return false
  e.preventDefault()
  return true
}

/** The dropdown under a text box: comic links in captions, existing tags in the Tags box */
function SuggestList<T>({
  id,
  label,
  heading,
  items,
  active,
  itemKey,
  renderItem,
  onPick,
}: {
  id: string
  label: string
  heading: ReactNode
  items: T[]
  active: number
  itemKey: (item: T) => string
  renderItem: (item: T) => ReactNode
  onPick: (item: T) => void
}) {
  return (
    <div className="suggest">
      <div className={items.length ? 'hint' : 'hint empty'}>{heading}</div>
      {items.length > 0 && (
        <ul role="listbox" id={id} aria-label={label}>
          {items.map((item, k) => (
            <li key={itemKey(item)} id={`${id}-opt-${k}`} role="option" aria-selected={k === active}>
              {/* preventDefault keeps focus in the text box, so the list doesn't close before the click lands */}
              <button type="button" className="sugg" aria-selected={k === active} tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(item)}>
                {renderItem(item)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Where an unfinished "[[" link starts before the caret, and what's typed after it */
export function openLink(text: string, caret: number): { start: number; query: string } | null {
  const before = text.slice(0, caret)
  const start = before.lastIndexOf('[[')
  if (start < 0) return null
  const query = before.slice(start + 2)
  if (query.includes(']]') || query.includes('\n') || query.length > 80) return null
  return { start, query }
}

/** Caption box: typing [[ suggests posted comics to link to */
export function CaptionField({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <ComicLinkField
      id="f-caption"
      label={
        <>
          Caption <span className="hint">(optional)</span>
        </>
      }
      hint="Type [[ to link to a comic you've already posted."
      value={value}
      onChange={onChange}
      disabled={disabled}
    />
  )
}

/** A text box where typing [[ suggests posted comics to link to */
export function ComicLinkField({
  id,
  label,
  hint,
  value,
  onChange,
  disabled,
  tall,
  invalid,
  inserts,
}: {
  id: string
  label: ReactNode
  hint: ReactNode
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  tall?: boolean
  invalid?: boolean
  /** Buttons that drop a snippet (like {{first name}}) in at the cursor */
  inserts?: Array<{ label: string; text: string }>
}) {
  const ref = useRef<HTMLTextAreaElement>(null)
  const [link, setLink] = useState<{ start: number; query: string } | null>(null)
  const [items, setItems] = useState<Suggestion[] | null>(null)
  const [active, setActive] = useState(0)

  useEffect(() => {
    if (!link) {
      setItems(null)
      return
    }
    const t = setTimeout(() => {
      api
        .get<{ items: Suggestion[] }>(`/api/admin/titles?q=${encodeURIComponent(link.query.split('|')[0].trim())}`)
        .then((r) => {
          setItems(r.items)
          setActive(0)
        })
        .catch(() => setItems([]))
    }, 150)
    return () => clearTimeout(t)
  }, [link])

  const update = (text: string, caret: number) => {
    onChange(text)
    setLink(openLink(text, caret))
  }

  const pick = (s: Suggestion) => {
    const el = ref.current
    if (!el || !link) return
    const caret = el.selectionStart
    const insert = `[[${s.title}]]`
    const text = value.slice(0, link.start) + insert + value.slice(caret)
    onChange(text)
    setLink(null)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(link.start + insert.length, link.start + insert.length)
    })
  }

  const insertAtCursor = (snippet: string) => {
    const el = ref.current
    if (!el) return
    const start = el.selectionStart
    const text = value.slice(0, start) + snippet + value.slice(el.selectionEnd)
    onChange(text)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(start + snippet.length, start + snippet.length)
    })
  }

  const onKey = (e: KeyboardEvent) => {
    if (link && items) suggestKeys(e, items.length, setActive, () => pick(items[active]), () => setLink(null))
  }

  const open = Boolean(link && items)
  return (
    <div className="formfield">
      <label htmlFor={id} className="lbl">
        {label}
      </label>
      <textarea
        id={id}
        ref={ref}
        className={`field ${tall ? 'tall' : 'short'}${invalid ? ' err' : ''}`}
        value={value}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={`${id}-hint`}
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={open ? `${id}-suggest` : undefined}
        aria-activedescendant={open && items!.length ? `${id}-suggest-opt-${active}` : undefined}
        onChange={(e) => update(e.target.value, e.target.selectionStart)}
        onKeyDown={onKey}
        onClick={(e) => setLink(openLink(value, e.currentTarget.selectionStart))}
        onBlur={() => setTimeout(() => setLink(null), 200)}
      />
      {inserts && (
        <div className="btns">
          {inserts.map((b) => (
            <button key={b.text} type="button" className="btn sm" disabled={disabled} onClick={() => insertAtCursor(b.text)}>
              {b.label}
            </button>
          ))}
        </div>
      )}
      <div id={`${id}-hint`} className="hint">
        {hint}
      </div>
      {open && (
        <SuggestList
          id={`${id}-suggest`}
          label="Comic suggestions"
          heading={items!.length ? 'Link to a comic' : 'No posted comics match.'}
          items={items!}
          active={active}
          itemKey={(s) => s.slug}
          renderItem={(s) => (
            <>
              {s.title}
              <span className="hint">{formatDate(s.date)}</span>
            </>
          )}
          onPick={pick}
        />
      )}
    </div>
  )
}

interface KnownTag {
  name: string
  count: number
}

/** Existing tags matching what's typed, not already on the comic: names starting with it first, then containing it */
export function matchTags(known: KnownTag[], typed: string, chosen: string[], max = 8): KnownTag[] {
  const q = typed.trim().toLowerCase()
  if (!q) return []
  const taken = new Set(chosen.map((t) => t.toLowerCase()))
  const hits = known.filter((t) => !taken.has(t.name.toLowerCase()) && t.name.toLowerCase().includes(q))
  const starts = (t: KnownTag) => (t.name.toLowerCase().startsWith(q) ? 0 : 1)
  return hits.sort((a, b) => starts(a) - starts(b) || b.count - a.count || a.name.localeCompare(b.name)).slice(0, max)
}

/** Tags: typing shows matching tags already in use to pick from, so the same tag isn't made twice */
export function TagField({ tags, onChange, disabled }: { tags: string[]; onChange: (tags: string[]) => void; disabled?: boolean }) {
  const [input, setInput] = useState('')
  const [known, setKnown] = useState<KnownTag[]>([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  useEffect(() => {
    api
      .get<{ items: KnownTag[] }>('/api/admin/tags')
      .then((r) => setKnown(r.items))
      .catch(() => {})
  }, [])

  const typed = input.trim().replace(/,$/, '').trim()
  const matches = matchTags(known, typed, tags)
  // Typing an existing tag in different capitals uses the existing spelling
  const existing = known.find((t) => t.name.toLowerCase() === typed.toLowerCase())
  const alreadyOn = tags.some((t) => t.toLowerCase() === typed.toLowerCase())
  const offerNew = Boolean(typed) && !existing && !alreadyOn
  // The options: matching tags, then "Add new tag" last
  const options: Array<{ name: string; count?: number }> = [...matches, ...(offerNew ? [{ name: typed }] : [])]
  const showing = open && Boolean(typed) && !disabled

  const addTag = (name: string) => {
    const v = known.find((t) => t.name.toLowerCase() === name.toLowerCase())?.name ?? name
    if (v && !tags.some((t) => t.toLowerCase() === v.toLowerCase())) onChange([...tags, v])
    setInput('')
    setActive(0)
  }

  const onKey = (e: KeyboardEvent) => {
    if (showing && suggestKeys(e, options.length, setActive, () => addTag(options[active].name), () => setOpen(false))) return
    if (e.key === 'ArrowDown') setOpen(true)
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      if (typed) addTag(typed)
    }
  }

  return (
    <div className="formfield">
      <label htmlFor="f-tag" className="lbl">
        Tags <span className="hint">(optional)</span>
      </label>
      <div className="hstack gap-md">
        <input
          id="f-tag"
          className="field grow"
          placeholder="Type a tag"
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={showing}
          aria-controls={showing ? 'f-tag-suggest' : undefined}
          aria-activedescendant={showing && options[active] ? `f-tag-suggest-opt-${active}` : undefined}
          value={input}
          disabled={disabled}
          onChange={(e) => {
            setInput(e.target.value)
            setOpen(true)
            setActive(0)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          onKeyDown={onKey}
        />
        <button type="button" className="btn" onClick={() => typed && addTag(typed)} disabled={disabled || !typed}>
          <Icon name="plus" size={18} />
          Add tag
        </button>
      </div>
      {showing && (
        <SuggestList
          id="f-tag-suggest"
          label="Tag suggestions"
          heading={matches.length ? 'Existing tags' : alreadyOn ? 'This comic already has this tag.' : 'No existing tags match.'}
          items={options}
          active={active}
          itemKey={(o) => (o.count === undefined ? `new-${o.name}` : o.name)}
          renderItem={(o) =>
            o.count === undefined ? (
              <span>
                Add new tag <strong>"{o.name}"</strong>
              </span>
            ) : (
              <>
                {o.name}
                <span className="hint">{o.count === 1 ? '1 comic' : `${o.count} comics`}</span>
              </>
            )
          }
          onPick={(o) => addTag(o.name)}
        />
      )}
      {tags.length > 0 && (
        <div className="chips mt-sm">
          {tags.map((t) => (
            <span key={t} className="tagchip">
              {t}
              <button type="button" aria-label={`Remove tag ${t}`} disabled={disabled} onClick={() => onChange(tags.filter((x) => x !== t))}>
                <Icon name="close" size={16} strokeWidth={2.5} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const STYLES: Array<{ value: DisplayStyle; label: string; hint: string }> = [
  { value: 'auto', label: 'Automatic', hint: "Long form on the comic's own page, carousel in the home and tag feeds." },
  { value: 'carousel', label: 'Carousel', hint: 'Readers swipe through the panels, everywhere.' },
  { value: 'longForm', label: 'Long form', hint: 'Panels stacked one under another, everywhere.' },
]

export function DisplayStyleField({ value, onChange, disabled }: { value: DisplayStyle; onChange: (v: DisplayStyle) => void; disabled?: boolean }) {
  return (
    <fieldset className="plain">
      <legend className="lbl">Display style</legend>
      {STYLES.map((s) => (
        <label key={s.value} className="opt">
          <input type="radio" name="f-style" value={s.value} checked={value === s.value} disabled={disabled} onChange={() => onChange(s.value)} />
          <span>
            <strong>{s.label}</strong>
            <span className="hint">{s.hint}</span>
          </span>
        </label>
      ))}
    </fieldset>
  )
}
